import { Sentry } from "@/lib/sentry";
import { supabase } from "@/integrations/supabase/client";

/**
 * Postgres / PostgREST error signatures that indicate the database (RLS) or
 * an RPC has rejected the call because the caller is not authorized.
 *
 *  - 42501 : insufficient_privilege  (RLS denial, missing GRANT)
 *  - PGRST301 : JWT-related / row not visible to role
 *  - PGRST116 : 0 rows returned where 1 expected (often RLS-induced)
 *  - PGRST301 / 401 / 403 : auth/permission rejections from PostgREST
 */
const DENIAL_CODES = new Set([
  "42501",
  "PGRST301",
  "PGRST302",
]);

const DENIAL_STATUSES = new Set([401, 403]);

type AnySupabaseError = {
  code?: string | null;
  message?: string | null;
  details?: string | null;
  hint?: string | null;
  status?: number | null;
} | null | undefined;

export type SecurityEventContext = {
  /** e.g. "rpc:admin_set_employee_security_code" or "table:employee_security_codes:select" */
  operation: string;
  /** Free-form extra data to attach to the Sentry event */
  extra?: Record<string, unknown>;
};

let cachedRole: string | null = null;
let cachedUserId: string | null = null;

async function getRoleContext(): Promise<{ userId: string | null; role: string | null }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { userId: null, role: null };
    if (user.id === cachedUserId && cachedRole) {
      return { userId: cachedUserId, role: cachedRole };
    }
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .order("role", { ascending: true })
      .limit(1)
      .maybeSingle();
    cachedUserId = user.id;
    cachedRole = (data?.role as string | undefined) ?? "unknown";
    return { userId: cachedUserId, role: cachedRole };
  } catch {
    return { userId: null, role: null };
  }
}

export function isAuthorizationError(error: AnySupabaseError): boolean {
  if (!error) return false;
  if (error.code && DENIAL_CODES.has(error.code)) return true;
  if (error.status && DENIAL_STATUSES.has(error.status)) return true;
  const msg = (error.message || "").toLowerCase();
  return (
    msg.includes("row-level security") ||
    msg.includes("permission denied") ||
    msg.includes("not authorized") ||
    msg.includes("violates row-level security")
  );
}

/**
 * Report an RLS denial / unauthorized RPC call to Sentry with role context.
 * Safe to call from anywhere — never throws.
 */
export async function reportSecurityDenial(
  error: AnySupabaseError,
  ctx: SecurityEventContext,
): Promise<void> {
  if (!error || !isAuthorizationError(error)) return;
  try {
    const { userId, role } = await getRoleContext();
    Sentry.withScope((scope) => {
      scope.setTag("security.event", "rls_or_rpc_denial");
      scope.setTag("security.operation", ctx.operation);
      if (role) scope.setTag("user.role", role);
      if (userId) scope.setUser({ id: userId });
      scope.setContext("supabase_error", {
        code: error.code ?? null,
        status: error.status ?? null,
        message: error.message ?? null,
        details: error.details ?? null,
        hint: error.hint ?? null,
      });
      if (ctx.extra) scope.setContext("extra", ctx.extra);
      Sentry.captureMessage(
        `Authorization denial on ${ctx.operation}`,
        "warning",
      );
    });
  } catch {
    /* never let telemetry break the calling code */
  }
}

/**
 * Wrap a Supabase RPC / query promise and forward auth denials to Sentry.
 * Returns the original result untouched.
 */
export async function withSecurityTelemetry<T extends { error: AnySupabaseError }>(
  promise: Promise<T>,
  ctx: SecurityEventContext,
): Promise<T> {
  const result = await promise;
  if (result?.error) {
    void reportSecurityDenial(result.error, ctx);
  }
  return result;
}
